(() => {
  'use strict';

  if (window.__storksSameDayController) {
    window.__storksSameDayController.initAll();
    return;
  }

  const activeWidgets = new Set();

  function turkeyParts() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());

    return Object.fromEntries(parts.map((part) => [part.type, part.value]));
  }

  function deliveryState(cutoffHour) {
    const parts = turkeyParts();
    const weekday = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))).getUTCDay();
    const weekend = weekday === 0 || weekday === 6;
    const nowSeconds = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    const cutoffSeconds = cutoffHour * 3600;
    const sameDay = nowSeconds < cutoffSeconds;
    const remaining = Math.max(0, cutoffSeconds - nowSeconds);
    const date = new Date(
      Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + (sameDay ? 0 : 1), 9)
    );
    const deliveryDate = new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    }).format(date);

    return { sameDay, remaining, cutoffSeconds, deliveryDate, weekend };
  }

  function durationText(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || root.dataset.initialized === 'true' || root.dataset.externalInitialized === 'true') return;

    const toggle = root.querySelector('[data-storks-delivery-toggle]');
    const panel = root.querySelector('[data-storks-delivery-panel]');
    const city = root.querySelector('[data-storks-delivery-city]');
    const district = root.querySelector('[data-storks-delivery-district]');
    const check = root.querySelector('[data-storks-delivery-check]');
    const result = root.querySelector('[data-storks-delivery-result]');
    const title = root.querySelector('[data-storks-delivery-title]');
    const summary = root.querySelector('[data-storks-delivery-summary]');
    const countdownLabel = root.querySelector('[data-storks-countdown-label]');
    const countdownTime = root.querySelector('[data-storks-countdown-time]');
    const countdown = root.querySelector('.storks-same-day__countdown');
    const progress = root.querySelector('[data-storks-progress]');
    const progressBar = root.querySelector('[data-storks-progress-bar]');
    const propertyMethod = root.querySelector('[data-storks-property-method]');
    const propertyCity = root.querySelector('[data-storks-property-city]');
    const propertyDistrict = root.querySelector('[data-storks-property-district]');
    const propertyDate = root.querySelector('[data-storks-property-date]');
    const shopLink = root.querySelector('[data-storks-shop-link]');

    const required = [toggle, panel, city, district, check, result, countdown, countdownLabel, countdownTime,
      progress, progressBar];
    if (required.some((element) => !element)) return;

    root.dataset.externalInitialized = 'true';
    const cutoffHour = Number(root.dataset.cutoffHour || 13);
    let verifiedDistrict = '';

    function clearProperties() {
      [propertyMethod, propertyCity, propertyDistrict, propertyDate].forEach((input) => {
        if (!input) return;
        input.value = '';
        input.disabled = true;
      });
      verifiedDistrict = '';
    }

    function setProperties(state) {
      if (!propertyMethod || !propertyCity || !propertyDistrict || !propertyDate) return;
      propertyMethod.value = 'İstanbul İçi Ücretsiz Kurye';
      propertyCity.value = 'İstanbul';
      propertyDistrict.value = district.value;
      propertyDate.value = state.deliveryDate;
      [propertyMethod, propertyCity, propertyDistrict, propertyDate].forEach((input) => { input.disabled = false; });
      verifiedDistrict = district.value;
    }

    function update() {
      const state = deliveryState(cutoffHour);
      countdown.hidden = state.weekend;
      if (title) title.textContent = 'İstanbul İçi Ücretsiz Kurye';
      if (summary) summary.textContent = "13:00'e kadar verilen siparişlerde";
      countdownLabel.textContent = 'Kalan süre';

      if (state.sameDay) {
        const percentage = Math.max(0, Math.min(100, (state.remaining / state.cutoffSeconds) * 100));
        const urgencyColor = `hsl(${Math.round(percentage * 1.2)} 68% 34%)`;
        countdownTime.textContent = durationText(state.remaining);
        countdownTime.style.color = urgencyColor;
        progressBar.style.width = `${percentage}%`;
        progressBar.style.backgroundColor = urgencyColor;
        progress.classList.remove('is-expired');
        progress.setAttribute('aria-valuenow', String(Math.round(percentage)));
        progress.setAttribute('aria-valuetext', `${durationText(state.remaining)} kaldı`);
      } else {
        countdownTime.textContent = '00:00:00';
        countdownTime.style.color = '#b3261e';
        progressBar.style.width = '0%';
        progressBar.style.backgroundColor = '#b3261e';
        progress.classList.add('is-expired');
        progress.setAttribute('aria-valuenow', '0');
        progress.setAttribute('aria-valuetext', 'Kalan süre 00:00:00');
      }

      if (verifiedDistrict && verifiedDistrict === district.value) {
        setProperties(state);
      }
    }

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      panel.hidden = open;
    });

    city.addEventListener('change', () => {
      district.disabled = city.value !== 'İstanbul';
      if (district.disabled) district.value = '';
      check.disabled = !city.value || !district.value;
      result.hidden = true;
      clearProperties();
    });

    district.addEventListener('change', () => {
      check.disabled = !city.value || !district.value;
      result.hidden = true;
      clearProperties();
    });

    shopLink?.addEventListener('click', (event) => {
      const productGrid = document.querySelector('.product-grid-container, [data-testid="product-grid"], .product-grid');
      if (!productGrid) return;
      event.preventDefault();
      productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    check.addEventListener('click', () => {
      if (city.value !== 'İstanbul' || !district.value) return;
      const state = deliveryState(cutoffHour);
      if (state.weekend) {
        clearProperties();
        result.className = 'storks-same-day__result';
        result.hidden = false;
        result.innerHTML = '<strong>Kurye hizmeti cumartesi, pazar ve resmî bayramlarda geçerli değildir.</strong>';
        return;
      }
      setProperties(state);
      result.className = 'storks-same-day__result is-success';
      result.hidden = false;
      result.innerHTML = `<strong>${district.value} adresine ücretsiz kurye teslimatı yapılabilir.</strong><br>13:00'e kadar verilen siparişlerde.`;
    });

    root.__storksSameDayUpdate = update;
    activeWidgets.add(root);
    update();
  }

  function initAll(scope = document) {
    if (scope instanceof Element && scope.matches('[data-storks-same-day]')) init(scope);
    scope.querySelectorAll?.('[data-storks-same-day]').forEach(init);
  }

  window.__storksSameDayController = { initAll };
  initAll();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initAll(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.setInterval(() => {
    activeWidgets.forEach((root) => {
      if (!root.isConnected) {
        activeWidgets.delete(root);
        return;
      }
      root.__storksSameDayUpdate?.();
    });
  }, 1000);
})();
